import React, { useEffect, useState } from 'react';
import {
  Card,
  Col,
  Form,
  Grid,
  Input,
  Row,
  Table,
  Modal,
  Tooltip,
  Button as AntButton,
} from 'antd';
import {
  Button,
  COLORS,
  Text,
  Title,
} from 'components';
import { scrollToTop } from 'components/ScrollToTop';
import {
  BarcodeOutlined,
  CloseCircleOutlined,
  DollarCircleOutlined,
  LoadingOutlined,
  SearchOutlined,
  CreditCardOutlined,
  MinusOutlined,
  SwapRightOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import NetPaybutton, {
  paymentLocationsEnum,
  validateLastTransaction,
} from 'views/Payments/components/NetPayButton';
import { useSelector } from 'react-redux';
import Previewer from 'components/Previewer';
import useQuery from 'hooks/useQuery';
import {
  getConvenios,
  getDatosDePagoConFolioDeOrdenDePago,
  getParcialidades,
  postCargosPorReferenciaDePago,
  postReferenciasBanco,
} from 'api/recaudacion';
import gettersMap from 'api/padrones';
import formatReference from 'utils/formatReference';
import { toCurrency } from 'utils/formatters';
import round from 'utils/round';
import Spinner from 'components/Spinner';
import ModalBancos from 'views/Payments/components/ModalBancos';
import {
  selectPadrones,
} from 'store/catalogos';
import logger from 'redux-logger';
import { DATETIME_FRONTEND_FORMAT } from 'utils/formatValues';

const PaymentOrder = () => {
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const [visiblePreviewRecibo, setVisiblePreviewRecibo] = useState(false);
  const [visiblePreviewOrden, setVisiblePreviewOrden] = useState(false);
  const [generandoRecibo, setGenerandoRecibo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referencia, setReferencia] = useState();
  const [recibo, setRecibo] = useState();
  const [folio] = useQuery(['folio']);
  const [cargos, setCargos] = useState([]);
  const [visibleCargos, setVisibleCargos] = useState(false);
  const [visibleBancos, setVisibleBancos] = useState(false);
  const [isNewFolio, setIsNewFolio] = useState(true);
  const [padronInfo, setPadronInfo] = useState({ id: null, tipo: null, info: {} });

  const [parcialidades, setParcialidades] = useState([]);

  const [total] = round(cargos?.reduce((accum, curr) => accum + curr.total_real, 0));

  const padrones = useSelector(selectPadrones);

  useEffect(() => {
    if (folio) {
      form.setFieldsValue({ folio });
      setTimeout(() => form.submit());
    }
  }, []);

  useEffect(() => {
    validateLastTransaction(false, async (_referencia) => {
      const _recibo = await formatReference(_referencia);
      if (_recibo) {
        setGenerandoRecibo(true);
        setTimeout(() => {
          setGenerandoRecibo(false);
          setRecibo(_recibo);
          setVisiblePreviewRecibo(true);
        }, 3000);
      }
    }, setLoading, paymentLocationsEnum.PAYMENT_ORDER, true, setGenerandoRecibo);
  }, []);

  const onCancel = () => {
    setVisiblePreviewRecibo(false);
    setVisiblePreviewOrden(false);
    setVisibleCargos(false);
    setVisibleBancos(false);
    setGenerandoRecibo(false);
    setIsNewFolio(true);
    setReferencia();
    setParcialidades([]);
    form.resetFields();
    scrollToTop(screens);
  };

  const onFinishSearch = async () => {
    try {
      setLoading(true);
      const { folio: _folio } = form.getFieldsValue();
      const _referencia = await getDatosDePagoConFolioDeOrdenDePago({ folio: _folio });
      if (_referencia !== null) {
        const _cargos = await postCargosPorReferenciaDePago({ folio: _folio });
        const _conveniosIds = [...new Set(_cargos.map((cargo) => cargo?.convenios))]
          .filter(Boolean);
        if (_conveniosIds.length) {
          const _convenios = await Promise.all(_conveniosIds.map((id) => getConvenios({ id })));
          const [convenio] = _convenios.flat();
          localStorage.setItem(`${paymentLocationsEnum.PAYMENT_ORDER}_CONVENIO`, convenio.id);
          const _parcialidades = await getParcialidades(convenio.id, true);
          if (_parcialidades.length) {
            const results = [];
            let resta = _referencia.importe;
            while (resta > 0) {
              const parcialidad = _parcialidades.shift();
              if (parcialidad) {
                resta -= parcialidad.importe_de_la_parcialidad;
                const _parcialidad = { ...parcialidad, abono: resta + parcialidad.adeudo };
                results.push(_parcialidad);
              }
            }
            setParcialidades(results);
          }
        }
        setCargos(_cargos);
        const padron = await gettersMap[_referencia.tipo_de_padron]({ id: _referencia.padron_id });
        if (padron) {
          _referencia.padron = padron;
          _referencia.folio = _folio;
          setReferencia(_referencia);
          setPadronInfo({
            id: _referencia.padron_id,
            tipo: _referencia.tipo_de_padron,
            info: { ...padron },
          });
        }
      }
      setLoading(false);
    } catch (error) {
      logger.error(error);
      setLoading(false);
    }
  };

  const generarReferenciaBanco = async (_cargos) => {
    setLoading(true);
    const cargosToReference = _cargos;
    const { padron, tipo_de_padron: tipoDePadron } = referencia;

    const _referencia = await postReferenciasBanco({
      tipoDePadron,
      padron,
      cargosToReference,
      padrones,
      cargos: _cargos,
      totalProp: 'total_real',
    });
    setIsNewFolio(false);
    setLoading(false);
    return _referencia;
  };

  const rules = {
    required: [{ required: true }],
  };

  const getBase64 = () => {
    if (visiblePreviewRecibo) {
      return recibo?.base64;
    }
    if (visiblePreviewOrden) {
      return referencia?.base64.at(-1) === ','
        ? referencia?.base64.substring(0, (referencia?.base64?.length ?? 0) - 1)
        : referencia?.base64;
    }
    return null;
  };

  const handleClickViewCargos = async () => {
    setVisibleCargos(true);
  };

  const parcialidadesColumns = [
    {
      title: 'Mensualidad',
      dataIndex: 'mensualidad',
      render: (val) => (val === 0 ? 'Anticipo' : val),
    },
    {
      title: 'Fecha de vencimiento',
      dataIndex: 'fecha_de_vencimiento',
      render: (val) => val?.format(DATETIME_FRONTEND_FORMAT),
    },
    {
      title: 'Importe de la parcialidad',
      dataIndex: 'importe_de_la_parcialidad',
      render: (val, record) => {
        const { abono } = record;
        const result = val - abono;
        if (abono > 0 && result > 0) {
          return (
            <Row justify="end" gutter={10}>
              <Col>
                <Tooltip title="Importe original">
                  <b style={{ color: COLORS.accent }}>
                    {toCurrency(val)}
                  </b>
                </Tooltip>
              </Col>
              <Col>
                <MinusOutlined />
              </Col>
              <Col>
                <Tooltip title="Abono">
                  <b className="success">
                    {toCurrency(abono)}
                  </b>
                </Tooltip>
              </Col>
              <Col>
                <SwapRightOutlined />
              </Col>
              <Col>
                <Tooltip title="Importe de la siguiente parcialidad">
                  <b className="success">
                    {toCurrency(result)}
                  </b>
                </Tooltip>
              </Col>
            </Row>
          );
        }

        return (
          <b style={{ color: record?.saldado ? '' : COLORS.accent }}>
            {toCurrency(val)}
          </b>
        );
      },
      align: 'right',
    },
  ];

  const cargosColumns = [
    {
      title: 'Descripcion',
      dataIndex: 'descripcion',
    },
    {
      title: 'Adeudo Total',
      dataIndex: 'total_real',
      render: (val) => <b style={{ color: COLORS.accent }}>{toCurrency(val)}</b>,
      align: 'right',
    },
  ];

  return (
    <>
      <Col span={24}>
        <Title style={{ textAlign: 'center', margin: 0 }}>
          Orden de Pago
        </Title>
      </Col>
      <Card
        title={(
          <Row justify={referencia ? 'space-between' : 'start'}>
            <Title level={2} $accent>
              Búsqueda
            </Title>
            {referencia && (
              <AntButton
                style={{ fontWeight: 'bold' }}
                onClick={onCancel}
                type="link"
                danger
              >
                <Text>Limpiar búsqueda</Text>
                <DeleteOutlined />
              </AntButton>
            )}
          </Row>
        )}
        style={{ width: '100%' }}
        bordered
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinishSearch}
          style={{ width: '100%' }}
        >
          <Row justify="end" gutter={[10, 10]}>
            <Col span={24}>
              <Form.Item
                label="Folio"
                name="folio"
                rules={rules.required}
              >
                <Input
                  disabled={!!referencia || loading}
                  suffix={loading ? <LoadingOutlined /> : null}
                />
              </Form.Item>
            </Col>
            {referencia ? (
              <>
                <NetPaybutton
                  referencia={referencia?.folio}
                  loading={loading}
                  cargos={cargos}
                  padron={referencia.padron}
                  createdBy="PAYMENT_ORDER"
                  totalProp="total_real"
                  total={parcialidades?.length ? referencia?.importe : total}
                />
                <Button
                  block={!screens.md}
                  onClick={handleClickViewCargos}
                  loading={loading}
                  $accent
                  style={{ marginRight: screens.md ? 5 : 0 }}
                >
                  Consultar
                  {parcialidades?.length ? ' parcialidades' : ' cargos'}
                  <DollarCircleOutlined />
                </Button>
                <Button
                  block={!screens.md}
                  onClick={() => setVisiblePreviewOrden(true)}
                  loading={loading}
                  $accent
                  style={{ marginRight: screens.md ? 5 : 0 }}
                >
                  Orden de pago
                  <BarcodeOutlined />
                </Button>
                <Col xs={24} sm={24} md={6}>
                  <Button
                    onClick={() => setVisibleBancos(true)}
                    loading={loading}
                    block
                    $accent
                  >
                    Transferencia bancaria
                    <CreditCardOutlined />
                  </Button>
                </Col>
                {/* <Button
                  block={!screens.md}
                  onClick={onCancel}
                  loading={loading}
                  // style={{ marginLeft: screens.md ? 5 : 0 }}
                  $accent
                >
                  Cancelar
                  <DeleteOutlined />
                </Button> */}
                {/** '23080000164' */}
              </>
            ) : (
              <Col>
                <Button
                  block={!screens.md}
                  htmlType="submit"
                  loading={loading}
                  $accent
                >
                  Buscar
                  <SearchOutlined />
                </Button>
              </Col>
            )}
          </Row>
        </Form>
      </Card>
      <Previewer
        visible={visiblePreviewRecibo || visiblePreviewOrden}
        base64={getBase64()}
        onCancel={() => {
          if (visiblePreviewOrden) {
            setVisiblePreviewRecibo(false);
            setVisiblePreviewOrden(false);
          } else {
            onCancel();
          }
        }}
      />
      <Modal
        maskClosable
        closable={false}
        visible={visibleCargos}
        title={(
          <Row justify="space-between">
            <Col>
              <Title level={2}>
                {parcialidades?.length ? 'Parcialidades ' : 'Cargos '}
                a pagar
              </Title>
            </Col>
            <Col>
              <Title level={2} $accent>
                {toCurrency(parcialidades?.length ? referencia?.importe : total)}
              </Title>
            </Col>
          </Row>
        )}
        centered
        width="80em"
        okText="Cerrar"
        footer={null}
      >
        <Table
          size="small"
          rowKey="id"
          dataSource={parcialidades?.length ? parcialidades : cargos}
          bordered
          pagination={{
            position: ['bottomLeft'],
          }}
          columns={parcialidades?.length ? parcialidadesColumns : cargosColumns}
        />
        <br />
        <Row justify="end">
          <Button
            onClick={() => setVisibleCargos(false)}
            $accent
          >
            <Text>Cerrar</Text>
            <CloseCircleOutlined />
          </Button>
        </Row>
      </Modal>
      <Spinner
        visible={generandoRecibo}
        loading={loading}
        generado={!!recibo}
        retryFunc={() => {
          window.location.reload();
        }}
      />
      <ModalBancos
        visible={visibleBancos}
        onCancel={() => { setVisibleBancos(false); }}
        cargosGenerados={cargos}
        padron={padronInfo.id}
        tipoDePadron={padronInfo.tipo}
        // currentClassifier={currentClassifier}
        padrones={padrones}
        cbReferenciaBanco={() => generarReferenciaBanco(cargos)}
        isNewFolio={isNewFolio}
        folio={referencia?.folio}
        totalProp="total_real"
      />
    </>
  );
};

export default PaymentOrder;
